import React, { useLayoutEffect, useRef } from 'react';
import './TabIndicator.scss';

const TRANSITION = 'transform 300ms cubic-bezier(.2, .7, .2, 1)';
const EXTEND = 6;

function readTarget(parent, active) {
  const parentRect = parent.getBoundingClientRect();
  const activeRect = active.getBoundingClientRect();
  const style = getComputedStyle(active);
  const padL = parseFloat(style.paddingLeft) || 0;
  const padR = parseFloat(style.paddingRight) || 0;
  const insetL = Math.max(0, padL - EXTEND);
  const insetR = Math.max(0, padR - EXTEND);
  return {
    left: activeRect.left - parentRect.left + parent.scrollLeft + insetL,
    width: Math.max(0, activeRect.width - insetL - insetR),
  };
}

export default function TabIndicator({ activeKey }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const line = ref.current;
    const parent = line?.parentElement;
    if (!parent || !line) return;

    let animating = false;
    let animTimer = 0;

    const findActive = () =>
      parent.querySelector('button[data-active="true"]') ||
      parent.querySelector('button.mode-tab-active') ||
      parent.querySelector('button.tab-active-acc') ||
      parent.querySelector('button.order-tab-active');

    const measure = (animate) => {
      if (!animate && animating) return;
      const active = findActive();
      if (!active || !active.offsetWidth) {
        line.style.opacity = '0';
        return;
      }
      line.style.opacity = '1';
      const target = readTarget(parent, active);
      if (target.width <= 0) return;

      const prevLeft = parseFloat(line.dataset.left || '');
      const prevWidth = parseFloat(line.dataset.width || '');
      const hasPrev = Number.isFinite(prevLeft) && Number.isFinite(prevWidth) && prevWidth > 0;

      line.dataset.left = String(target.left);
      line.dataset.width = String(target.width);

      if (!animate || !hasPrev || line.dataset.ready !== 'true') {
        line.style.transition = 'none';
        line.style.width = `${target.width}px`;
        line.style.transform = `translate3d(${target.left}px, 0, 0) scaleX(1)`;
        return;
      }

      const scale = prevWidth / target.width;
      line.style.transition = 'none';
      line.style.width = `${target.width}px`;
      line.style.transform = `translate3d(${prevLeft}px, 0, 0) scaleX(${scale})`;
      void line.offsetWidth;
      animating = true;
      window.clearTimeout(animTimer);
      line.style.transition = TRANSITION;
      line.style.transform = `translate3d(${target.left}px, 0, 0) scaleX(1)`;
      animTimer = window.setTimeout(() => { animating = false; }, 320);
    };

    measure(true);
    line.dataset.ready = 'true';

    const observer = new ResizeObserver(() => measure(false));
    observer.observe(parent);
    parent.querySelectorAll('button').forEach((button) => observer.observe(button));
    const onScroll = () => measure(false);
    parent.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.clearTimeout(animTimer);
      observer.disconnect();
      parent.removeEventListener('scroll', onScroll);
    };
  }, [activeKey]);

  return (
    <span
      ref={ref}
      aria-hidden="true"
      className="shared-tab-indicator"
    />
  );
}
